<template>
  <div class="dashboard-page">
    <div class="dashboard-container">

      <!-- Hero Banner -->
      <div class="hero-banner">
        <div class="hero-content">
          <div class="hero-text">
            <h1>Legislative Calendar</h1>
            <p>Department of Finance — 20th Congress Legislative Schedule</p>
          </div>
          <div class="session-info" v-if="currentSession.name !== 'Loading...'">
            <div class="session-badge-large">
              <i class="pi pi-calendar"></i>
              <span>{{ currentSession.name }}</span>
            </div>
            <div class="session-dates" v-if="currentSession.dateFrom">
              <span>{{ formatDate(currentSession.dateFrom) }} — {{ formatDate(currentSession.dateTo) }}</span>
            </div>
          </div>
        </div>
        <div class="hero-countdown" v-if="nextSession.name">
          <div class="countdown-label">Next: {{ nextSession.name }}</div>
          <div class="countdown-tiles">
            <div class="countdown-tile">
              <span class="countdown-number">{{ countdown.days }}</span>
              <span class="countdown-unit">Days</span>
            </div>
            <div class="countdown-tile">
              <span class="countdown-number">{{ countdown.hours }}</span>
              <span class="countdown-unit">Hours</span>
            </div>
            <div class="countdown-tile">
              <span class="countdown-number">{{ countdown.minutes }}</span>
              <span class="countdown-unit">Min</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Stats Cards -->
      <div class="stats-grid">
        <div class="stat-card" @click="isViewer ? null : showRequestForCommentsModal()">
          <div class="stat-icon" style="background: #dbeafe; color: #1e40af;">
            <i :class="isViewer ? 'pi pi-megaphone' : 'pi pi-file-edit'"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value" v-if="!loadingRequestForComments">{{ requestForComments || '—' }}</div>
            <div class="stat-value" v-else><ProgressSpinner style="width: 24px; height: 24px" strokeWidth="4" /></div>
            <div class="stat-label">{{ isViewer ? 'Legislative Update' : 'Request for Comments' }}</div>
          </div>
        </div>
        <div class="stat-card" @click="isViewer ? null : showSignedPositionModal()">
          <div class="stat-icon" style="background: #d1fae5; color: #065f46;">
            <i :class="isViewer ? 'pi pi-calendar' : 'pi pi-check-circle'"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value" v-if="!loadingSignedPosition">{{ signedPositionCount || '—' }}</div>
            <div class="stat-value" v-else><ProgressSpinner style="width: 24px; height: 24px" strokeWidth="4" /></div>
            <div class="stat-label">{{ isViewer ? 'Upcoming Hearings' : 'Signed Position Papers' }}</div>
          </div>
        </div>
        <div class="stat-card" @click="isViewer ? null : showForReviewsModal()">
          <div class="stat-icon" style="background: #fef3c7; color: #92400e;">
            <i :class="isViewer ? 'pi pi-book' : 'pi pi-clock'"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value" v-if="!loadingForReview">{{ forReviewCount || '—' }}</div>
            <div class="stat-value" v-else><ProgressSpinner style="width: 24px; height: 24px" strokeWidth="4" /></div>
            <div class="stat-label">{{ isViewer ? 'LEDAC Bills' : 'Ongoing Reviews' }}</div>
          </div>
        </div>
        <div class="stat-card" @click="showCommitteeReport">
          <div class="stat-icon" style="background: #ede9fe; color: #5b21b6;">
            <i class="pi pi-users"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">10</div>
            <div class="stat-label">Top Active Committees</div>
          </div>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="content-grid">

        <!-- Top 5 Urgent Bills -->
        <div class="content-section full-width">
          <div class="section-header">
            <h2><i class="pi pi-exclamation-triangle"></i> Top 5 Urgent Bills</h2>
            <Button label="Print" icon="pi pi-print" size="small" severity="secondary" @click="printTop5Urgent" />
          </div>
          <DataTable :value="top5UrgentBills" size="small" showGridlines scrollable scrollHeight="350px" stripedRows>
            <Column field="subject_title" header="Subject Title" style="min-width: 200px" />
            <Column field="urgency" header="Urgency" style="min-width: 90px">
              <template #body="{ data }">
                <Tag :value="data.urgency" :severity="getUrgencySeverity(data.urgency)" />
              </template>
            </Column>
            <Column field="bill_type" header="Bill Type" style="min-width: 100px" />
            <Column field="status" header="Status" style="min-width: 150px" />
            <Column field="total_revenue_impact" header="Revenue Impact" style="min-width: 160px" />
            <Column field="total_budgetary_impact" header="Budgetary Impact" style="min-width: 160px" />
            <Column field="fpmg_recommended_position" header="FPMG Position" style="min-width: 180px" />
            <Column header="" style="min-width: 70px">
              <template #body="{ data }">
                <Button icon="pi pi-eye" size="small" severity="info" @click="viewSubject(data)" v-tooltip="'View'" />
              </template>
            </Column>
          </DataTable>
        </div>

        <!-- News + Livestream row -->
        <div class="content-section">
          <div class="section-header">
            <h2><i class="pi pi-megaphone"></i> News Advisory</h2>
          </div>
          <div class="news-carousel">
            <Carousel :value="newsItems" :numVisible="1" :numScroll="1" :autoplayInterval="6000" :showIndicators="true" :showNavigators="newsItems.length > 1">
              <template #item="slotProps">
                <div class="news-slide">
                  <h3><a :href="slotProps.data.link" target="_blank" class="news-link">{{ slotProps.data.title }}</a></h3>
                  <p class="news-excerpt">{{ slotProps.data.excerpt }}</p>
                  <div class="news-meta">
                    <span class="news-date"><i class="pi pi-calendar"></i> {{ slotProps.data.date }}</span>
                    <Button label="Read More" size="small" icon="pi pi-external-link" @click="openNewsLink(slotProps.data.link)" />
                  </div>
                </div>
              </template>
            </Carousel>
          </div>
        </div>

        <div class="content-section">
          <div class="section-header">
            <h2><i class="pi pi-video"></i> Watch Livestream</h2>
          </div>
          <div class="stream-cards">
            <div class="stream-card" @click="openSenateStream">
              <div class="stream-icon">🏛️</div>
              <div class="stream-name">Senate Session</div>
              <div class="stream-live"><i class="pi pi-circle-fill"></i> Live</div>
            </div>
            <div class="stream-card" @click="openHouseStream">
              <div class="stream-icon">🏢</div>
              <div class="stream-name">House Session</div>
              <div class="stream-live"><i class="pi pi-circle-fill"></i> Live</div>
            </div>
          </div>
        </div>

        <!-- Committee Hearings -->
        <div class="content-section full-width">
          <div class="section-header">
            <h2><i class="pi pi-calendar"></i> Committee Hearings</h2>
            <div class="hearing-controls">
              <div class="period-buttons">
                <Button :label="'This Week'" size="small" :severity="hearingPeriod === 'this_week' ? undefined : 'secondary'" :loading="loadingHearings && hearingPeriod === 'this_week'" @click="loadHearings('this_week')" />
                <Button :label="'Next Week'" size="small" :severity="hearingPeriod === 'next_week' ? undefined : 'secondary'" :loading="loadingHearings && hearingPeriod === 'next_week'" @click="loadHearings('next_week')" />
                <Button :label="'Custom'" size="small" :severity="hearingPeriod === 'custom' ? undefined : 'secondary'" @click="showCustomDatePicker = !showCustomDatePicker" />
              </div>
            </div>
          </div>
          <div v-if="showCustomDatePicker" class="custom-date-row">
            <DatePicker v-model="customDateFrom" dateFormat="yy-mm-dd" placeholder="From" />
            <DatePicker v-model="customDateTo" dateFormat="yy-mm-dd" placeholder="To" />
            <Button label="Apply" size="small" icon="pi pi-check" @click="loadHearings('custom')" />
          </div>
          <div class="hearing-date-range" v-if="hearingDateFrom">
            <span><i class="pi pi-calendar"></i> {{ hearingDateFrom }} — {{ hearingDateTo }}</span>
            <span class="hearing-total">{{ hearingTotal }} hearing(s)</span>
          </div>
          <TabView>
            <TabPanel :header="'House (' + hearingHouse.length + ')'">
              <DataTable :value="hearingHouse" size="small" :paginator="hearingHouse.length > 5" :rows="5" stripedRows :loading="loadingHearings">
                <Column field="committee" header="Committee" />
                <Column field="agenda" header="Agenda" />
                <Column field="date" header="Date" style="width: 120px" />
                <Column field="time" header="Time" style="width: 100px" />
                <Column field="place" header="Place" />
              </DataTable>
              <div v-if="hearingHouse.length === 0 && !loadingHearings" class="empty-state">No House hearings for this period.</div>
            </TabPanel>
            <TabPanel :header="'Senate (' + hearingSenate.length + ')'">
              <DataTable :value="hearingSenate" size="small" :paginator="hearingSenate.length > 5" :rows="5" stripedRows :loading="loadingHearings">
                <Column field="committee" header="Committee" />
                <Column field="agenda" header="Agenda" />
                <Column field="date" header="Date" style="width: 120px" />
                <Column field="time" header="Time" style="width: 100px" />
                <Column field="place" header="Place" />
              </DataTable>
              <div v-if="hearingSenate.length === 0 && !loadingHearings" class="empty-state">No Senate hearings for this period.</div>
            </TabPanel>
            <TabPanel :header="'Other (' + hearingOther.length + ')'">
              <DataTable :value="hearingOther" size="small" :paginator="hearingOther.length > 5" :rows="5" stripedRows :loading="loadingHearings">
                <Column field="committee" header="Committee" />
                <Column field="agenda" header="Agenda" />
                <Column field="date" header="Date" style="width: 120px" />
                <Column field="time" header="Time" style="width: 100px" />
                <Column field="place" header="Place" />
              </DataTable>
              <div v-if="hearingOther.length === 0 && !loadingHearings" class="empty-state">No other hearings for this period.</div>
            </TabPanel>
          </TabView>
        </div>

        <!-- Quick Links -->
        <div class="content-section full-width">
          <div class="section-header">
            <h2><i class="pi pi-link"></i> Quick Links</h2>
          </div>
          <div class="quick-links-grid">
            <div class="quick-link-item">
              <i class="pi pi-book"></i>
              <span>LEDAC Bills</span>
            </div>
            <div class="quick-link-item" @click="openRedBills">
              <i class="pi pi-flag"></i>
              <span>RED Bills</span>
            </div>
            <div class="quick-link-item" @click="openAdvancingBills">
              <i class="pi pi-arrow-right"></i>
              <span>Advancing Bills</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Committee Report Dialog -->
    <Dialog v-model:visible="committeeReportVisible" header="Top 10 Active Committees" :style="{width: '60vw'}" :modal="true">
      <TabView>
        <TabPanel header="House">
          <DataTable :value="committeeReportHouse" :loading="loadingReport" :paginator="true" :rows="10" stripedRows>
            <Column field="name" header="Committee Name" />
            <Column field="total_count" header="Total Referrals" style="width: 150px; text-align: center;" />
          </DataTable>
        </TabPanel>
        <TabPanel header="Senate">
          <DataTable :value="committeeReportSenate" :loading="loadingReport" :paginator="true" :rows="10" stripedRows>
            <Column field="name" header="Committee Name" />
            <Column field="total_count" header="Total Referrals" style="width: 150px; text-align: center;" />
          </DataTable>
        </TabPanel>
      </TabView>
    </Dialog>

    <!-- Request for Comments Dialog -->
    <Dialog v-model:visible="requestForCommentsVisible" header="Request for Comments" :style="{width: '80vw'}" :modal="true">
      <DataTable :value="requestForCommentsList" :loading="loadingRequestForCommentsList" :paginator="true" :rows="10" v-model:filters="filters">
        <template #header>
          <div style="display: flex; justify-content: flex-end;">
            <InputText v-model="filters['global'].value" placeholder="Search..." />
          </div>
        </template>
        <Column field="requesting_party" header="Requesting Party" />
        <Column field="subject_title" header="Subject Title" />
        <Column field="agenda_link" header="Agenda Link">
          <template #body="{ data }">
            <a v-if="data.agenda_link" :href="data.agenda_link" target="_blank" class="doc-link">View</a>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="congressional_report_link" header="Congressional Report Link">
          <template #body="{ data }">
            <a v-if="data.congressional_report_link" :href="data.congressional_report_link" target="_blank" class="doc-link">View</a>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="draft_docket_link" header="Draft Docket Link">
          <template #body="{ data }">
            <a v-if="data.draft_docket_link" :href="data.draft_docket_link" target="_blank" class="doc-link">View</a>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
      </DataTable>
    </Dialog>

    <!-- For Reviews Dialog -->
    <Dialog v-model:visible="forReviewsVisible" header="Ongoing Reviews" :style="{width: '80vw'}" :modal="true">
      <DataTable :value="forReviewsList" :loading="loadingForReviewsList" :paginator="true" :rows="10" v-model:filters="filters">
        <template #header>
          <div style="display: flex; justify-content: flex-end;">
            <InputText v-model="filters['global'].value" placeholder="Search..." />
          </div>
        </template>
        <Column field="requesting_party" header="Requesting Party" />
        <Column field="subject_title" header="Subject Title" />
        <Column field="draft_position_paper_link" header="Draft Position Paper Link">
          <template #body="{ data }">
            <a v-if="data.draft_position_paper_link" :href="data.draft_position_paper_link" target="_blank" class="doc-link">View</a>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="draft_docket_link" header="Draft Docket Link">
          <template #body="{ data }">
            <a v-if="data.draft_docket_link" :href="data.draft_docket_link" target="_blank" class="doc-link">View</a>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
      </DataTable>
    </Dialog>

    <!-- Signed Position Papers Dialog -->
    <Dialog v-model:visible="signedPositionVisible" header="Signed Position Papers" :style="{width: '80vw'}" :modal="true">
      <DataTable :value="signedPositionList" :loading="loadingSignedPositionList" :paginator="true" :rows="10" v-model:filters="filters">
        <template #header>
          <div style="display: flex; justify-content: flex-end;">
            <InputText v-model="filters['global'].value" placeholder="Search..." />
          </div>
        </template>
        <Column field="requesting_party" header="Requesting Party" />
        <Column field="subject_title" header="Subject Title" />
        <Column field="signed_position_paper_link" header="Link of Signed Position Paper">
          <template #body="{ data }">
            <a v-if="data.signed_position_paper_link" :href="data.signed_position_paper_link" target="_blank" class="doc-link">View</a>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
      </DataTable>
    </Dialog>
  </div>
</template>

<script>
import Panel from 'primevue/panel'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Carousel from 'primevue/carousel'
import Tag from 'primevue/tag'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import Dialog from 'primevue/dialog'
import ProgressSpinner from 'primevue/progressspinner'
import api from '../axios.js'

export default {
  components: {
    Panel, Card, Button, Carousel, Tag, TabView, TabPanel,
    DataTable, Column, InputText, Dialog, ProgressSpinner
  },
  data() {
    return {
      searchTerm: '',
      hearingPeriod: 'this_week',
      hearingHouse: [],
      hearingSenate: [],
      hearingOther: [],
      hearingDateFrom: '',
      hearingDateTo: '',
      hearingTotal: 0,
      showCustomDatePicker: false,
      customDateFrom: null,
      customDateTo: null,
      loadingHearings: false,
      committeeReportVisible: false,
      committeeReportHouse: [],
      committeeReportSenate: [],
      loadingReport: false,
      signedIntoLawVisible: false,
      requestForComments: 0,
      signedPositionCount: 0,
      loadingRequestForComments: false,
      loadingSignedPosition: false,
      loadingForReview: false,
      forReviewCount: 0,
      requestForCommentsVisible: false,
      requestForCommentsList: [],
      loadingRequestForCommentsList: false,
      signedPositionVisible: false,
      signedPositionList: [],
      loadingSignedPositionList: false,
      forReviewsVisible: false,
      forReviewsList: [],
      loadingForReviewsList: false,
      signedIntoLawList: [],
      filters: { global: { value: null } },
      countdown: { days: 0, hours: 0, minutes: 0 },
      currentSession: { name: 'Loading...', dateFrom: null, dateTo: null },
      nextSession: { name: '', date: new Date() },
      newsItems: [],
      top5UrgentBills: []
    }
  },
  computed: {
    isViewer() {
      const roles = this.$authStore?.user?.role || []
      return roles.includes('viewer') && roles.length === 1
    }
  },
  methods: {
    async loadHearings(period) {
      this.hearingPeriod = period || 'this_week'
      this.loadingHearings = true
      let params = { period: this.hearingPeriod }
      if (this.hearingPeriod === 'custom' && this.customDateFrom && this.customDateTo) {
        params.start_date = this.customDateFrom instanceof Date ? this.customDateFrom.toISOString().split('T')[0] : this.customDateFrom
        params.end_date = this.customDateTo instanceof Date ? this.customDateTo.toISOString().split('T')[0] : this.customDateTo
        this.showCustomDatePicker = false
      }
      try {
        const response = await api.get('/dashboard/committee-hearings', { params })
        this.hearingHouse = response.data.house
        this.hearingSenate = response.data.senate
        this.hearingOther = response.data.other
        this.hearingDateFrom = response.data.date_from
        this.hearingDateTo = response.data.date_to
        this.hearingTotal = response.data.total
      } catch (e) {
        console.error(e)
        this.hearingHouse = []
        this.hearingSenate = []
        this.hearingOther = []
      } finally {
        this.loadingHearings = false
      }
    },
    async loadNews() {
      try {
        const response = await api.get('/news')
        this.newsItems = response.data.slice(0, 5).map(n => ({
          title: n.headline,
          excerpt: n.summary.length > 150 ? n.summary.substring(0, 150) + '...' : n.summary,
          link: n.news_link,
          date: n.date_published
        }))
      } catch (e) { this.newsItems = [] }
    },
    async loadCurrentSession() {
      try {
        const response = await api.get('/current-session')
        const data = response.data
        if (data.current) {
          this.currentSession = {
            name: `${data.current.congress_no}th Congress — ${data.current.session} Session${data.current.schedule ? ': ' + data.current.schedule : ''}`,
            dateFrom: data.current.date_from,
            dateTo: data.current.date_to
          }
        }
        if (data.next) {
          this.nextSession = { name: `${data.next.session} Session: ${data.next.schedule}`, date: new Date(data.next.date_from) }
        } else {
          this.nextSession = { name: '', date: new Date() }
        }
      } catch (e) {
        this.currentSession = { name: '20th Congress — First Session', dateFrom: null, dateTo: null }
        this.nextSession = { name: '', date: new Date() }
      }
    },
    async loadTop5Urgent() {
      try {
        const response = await api.get('/bills-by-urgency')
        this.top5UrgentBills = (response.data || []).slice(0, 5)
      } catch (e) { this.top5UrgentBills = [] }
    },
    updateCountdown() {
      const now = new Date().getTime()
      const distance = this.nextSession.date.getTime() - now
      if (distance > 0) {
        this.countdown.days = Math.floor(distance / (1000 * 60 * 60 * 24))
        this.countdown.hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        this.countdown.minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'TBD'
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    formatMeetingDate(dateString) {
      if (!dateString) return '—'
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatMeetingTime(timeString) {
      if (!timeString) return '—'
      const [h, m] = timeString.split(':')
      const hours = parseInt(h)
      return `${hours % 12 || 12}:${m} ${hours >= 12 ? 'PM' : 'AM'}`
    },
    openNewsLink(link) { window.open(link, '_blank') },
    openSenateStream() { window.open('https://www.youtube.com/@senateofthephilippines', '_blank') },
    openHouseStream() { window.open('https://www.youtube.com/@HouseofRepresentativesPH', '_blank') },
    openRedBills() { this.$router.push('/red-bills') },
    async openAdvancingBills() {
      try {
        const response = await api.get('/advancing-bills/print', { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
        window.open(url, '_blank')
      } catch (e) { console.error(e) }
    },
    async showCommitteeReport() {
      this.committeeReportVisible = true
      this.loadingReport = true
      try {
        const response = await api.get('/dashboard/top-committees')
        this.committeeReportHouse = response.data.house
        this.committeeReportSenate = response.data.senate
      } catch (e) { this.committeeReportHouse = []; this.committeeReportSenate = [] }
      finally { this.loadingReport = false }
    },
    async loadSignedPositionPaperCount() {
      this.loadingSignedPosition = true
      try {
        const response = await api.get('/dashboard/signed-position-papers')
        this.signedPositionCount = response.data.count
        this.signedPositionList = response.data.result
      } catch (e) { this.signedPositionCount = 0 }
      finally { this.loadingSignedPosition = false }
    },
    async loadRequestForCommentsCount() {
      this.loadingRequestForComments = true
      try {
        const response = await api.get('/dashboard/request-for-comments')
        this.requestForComments = response.data.count
        this.requestForCommentsList = response.data.result
      } catch (e) { this.requestForComments = 0 }
      finally { this.loadingRequestForComments = false }
    },
    async loadForReviewCount() {
      this.loadingForReview = true
      try {
        const response = await api.get('/dashboard/ongoing-reviews')
        this.forReviewCount = response.data.count
        this.forReviewsList = response.data.result
      } catch (e) { this.forReviewCount = 0 }
      finally { this.loadingForReview = false }
    },
    showRequestForCommentsModal() { this.requestForCommentsVisible = true; this.loadingRequestForCommentsList = false },
    showSignedPositionModal() { this.signedPositionVisible = true; this.loadingSignedPositionList = false },
    showForReviewsModal() { this.forReviewsVisible = true; this.loadingForReviewsList = false },
    hasValidUrl(linkString) {
      if (!linkString) return false
      try { return JSON.parse(linkString).Url?.trim() !== '' } catch { return false }
    },
    getUrlFromLink(linkString) {
      try { return JSON.parse(linkString).Url } catch { return '' }
    },
    getUrgencySeverity(urgency) {
      return { 'High': 'danger', 'Medium': 'warn', 'Low': 'info' }[urgency] || 'secondary'
    },
    viewSubject(data) { this.$router.push(`/subject/view/${data.id}`) },
    async printTop5Urgent() {
      try {
        const response = await api.get('/bills-by-subject/top5/urgent/print', { responseType: 'blob' })
        window.open(window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })), '_blank')
      } catch (e) { console.error(e) }
    }
  },
  async mounted() {
    this.loadNews()
    this.loadHearings('this_week')
    this.loadTop5Urgent()
    await this.loadCurrentSession()
    this.loadSignedPositionPaperCount()
    this.loadRequestForCommentsCount()
    this.loadForReviewCount()
    this.updateCountdown()
    setInterval(this.updateCountdown, 60000)
  }
}
</script>

<style scoped>
.dashboard-page {
  min-height: 100vh;
  background: #f1f5f9;
  padding: 1.5rem;
}

.dashboard-container {
  max-width: 1280px;
  margin: 0 auto;
}

/* Hero Banner */
.hero-banner {
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #3b82f6 100%);
  border-radius: 16px;
  padding: 2rem 2.5rem;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 10px 30px rgba(30, 58, 138, 0.3);
  gap: 2rem;
  flex-wrap: wrap;
}

.hero-text h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.hero-text p {
  opacity: 0.85;
  font-size: 0.95rem;
}

.session-info {
  margin-top: 1rem;
}

.session-badge-large {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
}

.session-info .session-dates {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  opacity: 0.8;
}

.hero-countdown {
  text-align: center;
}

.countdown-label {
  font-size: 0.8rem;
  opacity: 0.8;
  margin-bottom: 0.5rem;
}

.countdown-tiles {
  display: flex;
  gap: 0.75rem;
}

.countdown-tile {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  text-align: center;
  min-width: 60px;
}

.countdown-number {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fbbf24;
}

.countdown-unit {
  font-size: 0.7rem;
  text-transform: uppercase;
  opacity: 0.8;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e2e8f0;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(30, 58, 138, 0.12);
  border-color: #3b82f6;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.stat-label {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 0.25rem;
  font-weight: 500;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.content-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
}

.content-section.full-width {
  grid-column: 1 / -1;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e2e8f0;
}

.section-header h2 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e3a8a;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

/* News */
.news-carousel {
  min-height: 180px;
}

.news-slide {
  padding: 1rem 0;
}

.news-slide h3 {
  font-size: 1rem;
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

.news-link {
  color: #1e3a8a;
  text-decoration: none;
}

.news-link:hover {
  text-decoration: underline;
}

.news-excerpt {
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 0.75rem;
}

.news-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.news-date {
  color: #94a3b8;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

/* Streams */
.stream-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stream-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e2e8f0;
}

.stream-card:hover {
  background: #fee2e2;
  border-color: #fca5a5;
}

.stream-icon {
  font-size: 1.5rem;
}

.stream-name {
  flex: 1;
  font-weight: 600;
  color: #1e293b;
}

.stream-live {
  color: #dc2626;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Hearings */
.hearing-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.period-buttons {
  display: flex;
  gap: 0.25rem;
}

.custom-date-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.hearing-date-range {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  margin-bottom: 1rem;
  background: #eff6ff;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #1e40af;
}

.hearing-total {
  font-weight: 600;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #94a3b8;
  font-size: 0.9rem;
}

/* Quick Links */
.quick-links-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.quick-link-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: #f8fafc;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e2e8f0;
  font-weight: 600;
  color: #1e3a8a;
}

.quick-link-item:hover {
  background: #1e3a8a;
  color: white;
  border-color: #1e3a8a;
}

.quick-link-item i {
  font-size: 1.1rem;
}

/* Dialog */
.doc-link {
  color: #1e3a8a;
  font-weight: 500;
  text-decoration: none;
}

.doc-link:hover {
  text-decoration: underline;
}

.text-muted {
  color: #94a3b8;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-page {
    padding: 1rem;
  }

  .hero-banner {
    flex-direction: column;
    text-align: center;
    padding: 1.5rem;
  }

  .hero-text h1 {
    font-size: 1.4rem;
  }

  .content-grid {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }

  .countdown-tiles {
    justify-content: center;
  }
}
</style>
